import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRoute, Router, RouterStateSnapshot } from "@angular/router";
import { ModalService } from "@root/router/index/modal.service";
import { DateService } from "@root/shared/service/date.service";
import { NzModalService } from "ng-zorro-antd/modal";
import { Observable, tap } from "rxjs";

export enum Type {
  Upgrade = 1,
  Renew = 2,
}

export enum PlanEdition {
  Trial = 0, // 试用版
  Standard = 1,
  Pro = 2,
  Enterprise = 3,
}

@Injectable({
  providedIn: "root",
})
export class SubscriptionService {
  private readonly PlanOverviewApi = "/api/subscription/plan/overview";
  private readonly PlanPriceApi = "/api/member/order/pricing";
  private readonly PlanCheckoutApi = "/api/member/order/subscribe";
  private readonly PlanChangePeriodApi = "/api/member/order/change_subscription_period";
  private readonly PlanPriceListApi = "/api/member/price_list_v4";

  private readonly InvoiceListApi = "/api/subscription/invoice/list";

  private readonly BillingInfoSaveApi = "/api/subscription/billing_info/save";
  private readonly BillingInfoGetApi = "/api/subscription/billing_info/get";
  private readonly BillingConfigApi = "/api/subscription/billing_info/config";

  private readonly PaymentMethodCreateApi = "/api/subscription/payment_method/create";
  private readonly PaymentMethodListApi = "/api/subscription/payment_method/list";
  private readonly PaymentMethodPrimaryApi = "/api/subscription/payment_method/set_primary";
  private readonly PaymentMethodDeleteApi = "/api/subscription/payment_method/delete";

  private readonly ReferSendEmailApi = "/api/subscription/invite/send_email";
  private readonly ReferCreditApi = "/api/subscription/invite/me";
  private readonly ReferHistoryApi = "/api/subscription/invite/refer_list";

  private readonly BalanceOverviewApi = "/api/balance/order/overview";
  propertyCount = 0; // 账号房间数量。目前适用于试用期的用户
  operator; // 当前用户信息
  currentPlan; // 当前合约
  subscription; // 主要包含订阅周期相关内容，与currenPlan里的可能不同
  nextPlan; // 下一个合约
  nextCharge; // 下一个支付

  get isTrialEdition() {
    return this.currentPlan?.edition === PlanEdition.Trial;
  }

  get isProEdition() {
    return this.currentPlan?.edition === PlanEdition.Pro;
  }

  get isStandardEdition() {
    return this.currentPlan?.edition === PlanEdition.Standard;
  }

  get isExpired() {
    return this.currentPlan?.is_expired;
  }

  get isPropertyExceedLimit() {
    return this.propertyCount > this.currentPlan?.quantity;
  }

  constructor(
    private http: HttpClient,
    private modal: NzModalService,
    private globalModal: ModalService,
    private dateSrv: DateService,
    private router: Router,
  ) {}

  initPlanOverview() {
    return this.http.get(this.PlanOverviewApi).pipe(
      tap((rst: any) => {
        this.propertyCount = rst?.data?.property_count;
        this.operator = rst?.data?.operator;
        this.currentPlan = rst?.data?.current_plan;
        this.subscription = rst?.data?.subscription;
        this.nextPlan = rst?.data?.next_plan;
        this.nextCharge = rst?.data?.next_charge;

        /** 埋点 */
        try {
          window["heap"]?.addUserProperties({ plan_edition: this.isTrialEdition ? "试用版" : "专业版" });
        } catch (e) {}
      }),
    );
  }

  checkSubscriptionStatus() {
    this.initPlanOverview().subscribe((rst: any) => {
      const url = this.router.url;
      if (this.inBlockPathList(url)) {
        if (this.isPropertyExceedLimit) {
          return this.displayPropertyExceedWarning();
        }
        if (this.isExpired) {
          return this.displayExpiredWarning();
        }
      }
    });
  }

  displayPropertyExceedWarning() {
    const modals = this.modal.openModals;
    if (modals?.length) {
      return;
    }
    this.modal.warning({
      nzClosable: false,
      nzWidth: 500,
      nzTitle: `Hi ${this.operator?.name}, you have ${this.propertyCount} properties since ${this.dateSrv.format(
        new Date(),
        ["YMD"],
      )}, which exceed the number of properties included in your subscription. Please upgrade your subscription to resume.`,
      nzOkText: "Upgrade",
      nzOnOk: () => {
        this.router.navigate(["/app/subscription/manage-subscription"], { queryParams: { change: "quantity" } });
      },
    });
  }

  displayExpiredWarning() {
    const modals = this.modal.openModals;
    if (modals?.length) {
      return;
    }
    this.modal.warning({
      nzClosable: false,
      nzWidth: 500,
      nzTitle: `Hi ${this.operator?.name}, you need to subscribe before you can continue to use the product.`,
      nzOkText: "Subscribe",
      nzCancelText: "Contact us",
      nzOnOk: () => {
        this.router.navigate(["/app/subscription/your-plan"]);
      },
      nzOnCancel: () => {
        this.router.navigate(["/app/subscription/your-plan"]);
        setTimeout(() => {
          this.globalModal.displayCustomerServiceClient();
          // LiveChatWidget?.call('maximize');
        }, 1000);
      },
    });
  }

  displayVrboConnectForbiddenWarning() {
    this.modal.warning({
      nzClosable: false,
      nzWidth: 500,
      nzTitle: `You need to upgrade to the Pro Edition before integrating with Vrbo.`,
      nzOkText: "Upgrade",
      nzCancelText: "Got it",
      nzOnOk: () => {
        this.router.navigate(["/app/subscription/upgrade"]);
      },
    });
  }

  displaySeamLockForbiddenWarning() {
    this.modal.warning({
      nzClosable: false,
      nzWidth: 500,
      nzTitle: `You need to upgrade to the Pro Edition before integrating with this smart lock via Seam.`,
      nzOkText: "Upgrade",
      nzCancelText: "Got it",
      nzOnOk: () => {
        this.router.navigate(["/app/subscription/upgrade"]);
      },
    });
  }

  private inBlockPathList(path) {
    return !["/app/subscription", "/app/connected-accounts", "/app/settings", "/app/properties", "/app/guide"].some(
      (router) => path.startsWith(router),
    );
  }

  requestGetPrice(params) {
    return this.http.get(this.PlanPriceApi, { params });
  }

  requestCheckout(params) {
    return this.http.post(this.PlanCheckoutApi, params);
  }

  requestUpgrade(params) {
    return this.http.post("/api/member/order/upgrade_subscription_edition", params);
  }

  requestUpgradeQuantity(params) {
    return this.http.post("/api/member/order/upgrade_subscription_quantity", params);
  }

  requestChangePeriod(params) {
    return this.http.post(this.PlanChangePeriodApi, params);
  }

  fetchInvoiceList(params) {
    return this.http.get(this.InvoiceListApi, { params });
  }

  fetchPriceList() {
    return this.http.get(this.PlanPriceListApi);
  }

  // billing
  requestSaveBillingInfo(data) {
    return this.http.post(this.BillingInfoSaveApi, data);
  }

  fetchBillingInfo() {
    return this.http.get(this.BillingInfoGetApi);
  }

  fetchBillingConfig() {
    return this.http.get(this.BillingConfigApi);
  }

  // payment method
  requestCreatePaymentMethod(params) {
    return this.http.post(this.PaymentMethodCreateApi, params);
  }

  fetchPaymentMethodList(params) {
    return this.http.get(this.PaymentMethodListApi, { params });
  }

  requestSetPaymentMethodPrimary(params) {
    return this.http.post(this.PaymentMethodPrimaryApi, params);
  }

  requestDeletePaymentMethod(params) {
    return this.http.post(this.PaymentMethodDeleteApi, params);
  }

  // refer
  requestSendReferEmails(params) {
    return this.http.post(this.ReferSendEmailApi, params);
  }
  fetchReferCredit(params?) {
    return this.http.get(this.ReferCreditApi, { params });
  }
  fetchReferHistory(params) {
    return this.http.get(this.ReferHistoryApi, { params });
  }

  //#region Balance

  fetchBalanceOverview(): Observable<HttpRspGen<BalanceOverview>> {
    return this.http.get(this.BalanceOverviewApi) as Observable<HttpRspGen<BalanceOverview>>;
  }

  requestBalanceRecharge(params) {
    return this.http.post("/api/balance/order/charge", params);
  }

  requestDisableAutoRecharge() {
    return this.http.post("/api/balance/order/disable_auto_charge", null);
  }

  fetchTransactionList(params) {
    return this.http.get("/api/balance/transaction/list", { params });
  }

  //#endregion

  clear() {
    this.propertyCount = 0; // 账号房间数量。目前适用于试用期的用户
    this.operator = null; // 当前用户信息
    this.currentPlan = null; // 当前合约
    this.subscription = null; // 主要包含订阅周期相关内容，与currenPlan里的可能不同
    this.nextPlan = null; // 下一个合约
    this.nextCharge = null; // 下一个支付
  }
}

export interface BalanceOverview {
  balance: number;
  currency: string;
  auto_recharge: {
    charge_amount: number;
    enabled: boolean;
    threshold: number;
  };
}
